/**
 * Developer Help recipe page — renders one "Cortex Recipe Card" by :slug.
 * Ingredients, numbered steps with copyable multi-language code, taste test,
 * common burns, and a link to the next recipe in the journey.
 */

import { useMemo } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft as ArrowBackIcon, ArrowRight as ArrowForwardIcon, CircleCheck as CheckIcon, CircleAlert as BurnIcon, Circle as IngredientIcon } from 'lucide-react';

import { Button as UiButton, Chip, ShellHeader } from '@coordio/ui';
import { FormSection, PageContainer } from '@coordio/ui/layout';

import { getEffectiveApiUrl } from '../config/runtimeEnv';
import { getRecipes } from '../content/developerHelp/recipes';
import { CodeSampleBlock, Section } from '../components/developerHelp/RecipePrimitives';
import { ROUTES } from '../utils/routes';

export const DeveloperHelpRecipePage = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();

  const baseUrl = useMemo(() => {
    const apiUrl = getEffectiveApiUrl();
    if (apiUrl.startsWith('http')) return apiUrl.replace(/\/api\/?$/, '');
    return typeof window !== 'undefined' ? window.location.origin : 'https://your-agentops-host';
  }, []);

  const recipes = useMemo(() => getRecipes(baseUrl), [baseUrl]);
  const recipe = recipes.find((r) => r.slug === slug);

  if (!recipe) {
    return <Navigate to={ROUTES.DEVELOPER_HELP} replace />;
  }

  const nextRecipe = recipe.nextSlug ? recipes.find((r) => r.slug === recipe.nextSlug) : undefined;

  return (
    <div className="developer-help-recipe" data-testid="developer-help-recipe-form-page">
      <ShellHeader
        title={recipe.title}
        description={`Goal: ${recipe.goal}`}
        data-testid="developer-help-recipe-header"
        actions={
          <UiButton
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate(ROUTES.DEVELOPER_HELP)}
            data-testid="back-developer-help-recipe-btn"
          >
            <ArrowBackIcon size={16} />
            All guides
          </UiButton>
        }
      />

      <PageContainer data-testid="developer-help-recipe-page-container">
        <div className="flex items-center gap-1 mb-3">
          <Chip variant="outlined" color="accent" label={`Recipe ${recipe.number}`} />
          <Chip variant="outlined" label={recipe.time} />
        </div>

      <Section title="Ingredients">
        <ul className="space-y-2">
          {recipe.ingredients.map((ingredient) => (
            <li key={ingredient} className="flex items-center gap-2">
              <IngredientIcon size={16} className="text-gray-400" />
              <span className="text-sm font-medium">{ingredient}</span>
            </li>
          ))}
        </ul>
      </Section>

      {recipe.steps.map((step, index) => (
        <Section key={step.title} title={`Step ${index + 1} — ${step.title}`}>
          <p className="text-sm text-[var(--color-text-secondary)]">
            {step.body}
          </p>
          {step.code && <CodeSampleBlock sample={step.code} />}
        </Section>
      ))}

      <Section title="Taste test — did it work?">
        <ul className="space-y-2">
          {recipe.tasteTest.map((item) => (
            <li key={item} className="flex items-center gap-2">
              <CheckIcon size={16} className="text-green-500" />
              <span className="text-sm font-medium">{item}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Common burns">
        <ul className="space-y-2">
          {recipe.burns.map((burn) => (
            <li key={burn} className="flex items-center gap-2">
              <BurnIcon size={16} className="text-amber-500" />
              <span className="text-sm font-medium">{burn}</span>
            </li>
          ))}
        </ul>
      </Section>

        {nextRecipe ? (
          <UiButton
            type="button"
            variant="default"
            className="gap-2"
            onClick={() => navigate(`${ROUTES.DEVELOPER_HELP}/${nextRecipe.slug}`)}
            data-testid="developer-help-recipe-next-btn"
          >
            Next: Recipe {nextRecipe.number} — {nextRecipe.title}
            <ArrowForwardIcon size={16} />
          </UiButton>
        ) : (
          <FormSection title="Journey complete" data-testid="developer-help-recipe-section-journey-complete">
            <p className="text-sm text-[var(--color-text-secondary)]">
              You have completed the journey — your agent is created, capable, and connected. Explore the API
              Explorer for per-agent request builders, or dive into the deeper references in the docs.
            </p>
          </FormSection>
        )}
      </PageContainer>
    </div>
  );
};
